import { BuildClass, Do, fsmData, } from '../../../universal';
import { _, React } from '../../lib';
import { AddButton, Button } from './buttons';
import { Checkbox } from './checkbox';
import { Combobox } from './combobox';
import { DateBox } from './date';
import { KebabMenuToolbar } from './kebab';
import { CJSX } from './meta-types';
import { stubDiv } from './stubs';
import { SearchBox, Textbox } from './textbox';
import { useResizeObserver } from './wrappers';
export const Toolbar = React.forwardRef((props, ref) => {
    if ((props.widthLHS == null) == (props.widthRHS == null)) {
        console.trace('Exactly one width should be defined');
    }
    if (!props.lhs && !props.rhs) {
        console.trace('No LHS/RHS defined for the toolbar');
    }
    const widths = props.widthLHS != null
        ? { lhs: `${props.widthLHS}px`, rhs: `calc(100% - ${props.widthLHS}px)` }
        : { lhs: `calc(100% - ${props.widthRHS}px)`, rhs: `${props.widthRHS}px` };
    return (React.createElement("div", { ref: ref, className: BuildClass({
            [BuildClass(props.className)]: true,
            'ui5-toolbar': true,
            'tailwind-wrapper': !(props.omitTailwindWrapper ?? false),
        }) },
        React.createElement("div", { className: "lhs side", style: { width: widths.lhs } }, props.lhs),
        React.createElement("div", { className: "rhs side", style: { width: widths.rhs } }, props.rhs)));
});
export const ToolbarNew = (props) => {
    const ITEM_MARGIN = 5;
    const OVERFLOW_BUTTON_WIDTH = 45;
    const BOX_PADDING_X = 5;
    const container = React.useRef(stubDiv);
    const rect = useResizeObserver(container.current);
    const available_width = rect.width - BOX_PADDING_X * 2;
    const [_inc, setInc] = React.useState(0);
    const layout = React.useMemo(() => {
        const addSide = (items, side) => _.chain(items ?? [])
            .compact()
            .map((item, index) => ({
            side,
            item,
            index,
            margin: index > 0 ? ITEM_MARGIN : 0,
        }))
            .value();
        const all_items = _.chain([
            ...addSide(props.lhs, 'left'),
            ...addSide(props.rhs, 'right'),
        ])
            .filter(x => x.item.visible ?? true)
            .sortBy(x => x.item.importance ?? 0)
            .reverse()
            .value();
        const total_required_width = _.chain(all_items)
            .filter(x => !x.item.alwaysCollapse)
            .map(item => item.item.width + item.margin)
            .sum()
            .value();
        const overflow_needed = _.some([
            all_items.find(x => x.item.alwaysCollapse) != null,
            available_width < total_required_width,
        ]);
        const overflow_button_width = overflow_needed
            ? OVERFLOW_BUTTON_WIDTH + ITEM_MARGIN
            : 0;
        const remaining_width = available_width - overflow_button_width;
        const sortedItems = _.chain(all_items)
            .filter(x => !x.item.alwaysCollapse)
            .sortBy(x => x.item.importance ?? 0)
            .reverse()
            .value();
        const items = Do(() => {
            let currentWidth = 0;
            const items = [];
            const collapsed = [];
            for (const item of sortedItems) {
                const newWidth = currentWidth + item.item.width + item.margin;
                if (newWidth < remaining_width) {
                    items.push(item);
                    currentWidth = newWidth;
                }
                else {
                    collapsed.push(item);
                }
            }
            return {
                lhs: fsmData(items, {
                    filter: x => x.side == 'left',
                    sort: x => x.index,
                    map: x => x,
                }),
                rhs: fsmData(items, {
                    filter: x => x.side == 'right',
                    sort: x => x.index,
                    map: x => x,
                }),
                collapsed: fsmData(collapsed, {
                    filter: x => x.item.overflowVisible ?? true,
                    sort: x => x.index,
                    map: x => x,
                }),
            };
        });
        const rawWidthLHS = _.sum(items.lhs.map(x => x.item.width + x.margin));
        const rawWidthRHS = _.sum(items.rhs.map(x => x.item.width + x.margin)) + overflow_button_width;
        const split = (available_width - rawWidthLHS - rawWidthRHS) / 2;
        const widthLHS = Math.floor(rawWidthLHS + split);
        const widthRHS = Math.floor(rawWidthRHS + split);
        return { items, widthLHS, widthRHS };
    }, [available_width, props.lhs, props.rhs]);
    return (React.createElement(Toolbar, { ref: container, className: props.className, widthLHS: layout.widthLHS, lhs: React.createElement(React.Fragment, null, layout.items.lhs.map((x, index) => (React.createElement(ToolbarItem, { ...x.item, key: index })))), rhs: React.createElement(React.Fragment, null,
            layout.items.rhs.map((x, index) => (React.createElement(ToolbarItem, { ...x.item, key: index }))),
            React.createElement(CJSX, { cond: layout.items.collapsed.length > 0 },
                React.createElement(KebabMenuToolbar, { items: () => layout.items.collapsed.map(x => getToolbarItemOverflowed(x.item, () => {
                        setInc(s => s + 1);
                    })) }))), omitTailwindWrapper: false }));
};
const getToolbarItemOverflowed = (item, reflow) => {
    if (item.type == 'combobox') {
        return {
            label: 'COMBOBOX TODO',
        };
    }
    if (item.type == 'combobox-multi') {
        return {
            label: 'COMBOBOX MULTI TODO',
        };
    }
    if (item.type == 'combobox-multi-strkey') {
        return {
            label: 'COMBOBOX MULTI STRKEY TODO',
        };
    }
    if (item.type == 'combobox-strkey') {
        return {
            label: 'COMBOBOX STRKEY TODO',
        };
    }
    if (item.type == 'datebox') {
        return {
            label: 'DATEBOX TODO',
        };
    }
    if (item.type == 'textbox') {
        return {
            label: 'TEXTBOX TODO',
        };
    }
    if (item.type == 'searchbox') {
        return {
            label: 'SEARCHBOX TODO',
        };
    }
    if (item.type == 'button' || item.type == 'button-add' || item.type == 'button-24') {
        return {
            label: item.lbl ?? (item.type == 'button-add' ? 'Add' : ''),
            labelDesc: item.title,
            icon: item.type == 'button-add' ? '/static/img/i8/w11-add.svg' : item.img,
            onClick: item.onClick,
        };
    }
    if (item.type == 'checkbox') {
        return {
            label: item.lbl,
            icon: () => item.value ? '/static/img/i8/material-outline-checkmark.svg' : undefined,
            dontClose: true,
            labelDesc: item.title,
            onClick: () => {
                item.onUpdate?.(!item.value);
                reflow();
            },
        };
    }
    if (item.type == 'label') {
        return { label: item.lbl, labelDesc: item.tooltip };
    }
    if (item.type == 'separator') {
        return '---';
    }
    if (item.type == 'raw-jsx') {
        return item.overflowOverride;
    }
    const _exhaustiveCheck = item;
    throw new Error(`Unknown toolbar item type: ${String(_exhaustiveCheck['type'])}`);
};
const ToolbarItem = (props) => {
    const exclKeys = ['type', 'importance', 'alwaysCollapse', 'visible', 'width'];
    if (props.type == 'combobox') {
        return (React.createElement(Combobox, { ..._.omit(props, exclKeys), multiple: false, style: { width: `${props.width}px` } }));
    }
    if (props.type == 'combobox-strkey') {
        return (React.createElement(Combobox, { ..._.omit(props, exclKeys), multiple: false, style: { width: `${props.width}px` } }));
    }
    if (props.type == 'combobox-multi') {
        return (React.createElement(Combobox, { ..._.omit(props, exclKeys), multiple: true, style: { width: `${props.width}px` } }));
    }
    if (props.type == 'combobox-multi-strkey') {
        return (React.createElement(Combobox, { ..._.omit(props, exclKeys), multiple: true, style: { width: `${props.width}px` } }));
    }
    if (props.type == 'textbox') {
        return (React.createElement(Textbox, { ..._.omit(props, exclKeys), style: { width: `${props.width}px` } }));
    }
    if (props.type == 'searchbox') {
        return (React.createElement(SearchBox, { ..._.omit(props, exclKeys), style: { width: `${props.width}px` } }));
    }
    if (props.type == 'datebox') {
        return (React.createElement(DateBox, { ..._.omit(props, exclKeys), style: { width: `${props.width}px` } }));
    }
    if (props.type == 'button') {
        return React.createElement(Button, { type: "borderless", ..._.omit(props, exclKeys) });
    }
    if (props.type == 'button-24') {
        return React.createElement(Button, { type: "ui5-borderless-24", ..._.omit(props, exclKeys) });
    }
    if (props.type == 'button-add') {
        return React.createElement(AddButton, { ..._.omit(props, exclKeys) });
    }
    if (props.type == 'checkbox') {
        return React.createElement(Checkbox, { ..._.omit(props, exclKeys) });
    }
    if (props.type == 'label') {
        return (React.createElement("span", { className: BuildClass(['text', props.className]), title: props.tooltip }, props.lbl));
    }
    if (props.type == 'separator') {
        return React.createElement(React.Fragment, null);
    }
    if (props.type == 'raw-jsx') {
        return props.content;
    }
    const _exhaustiveCheck = props;
    throw new Error(`Unknown toolbar item type: ${String(_exhaustiveCheck['type'])}`);
};
//# sourceMappingURL=toolbar.js.map